/**
 * Document shape resolution — the single source of truth for "what does this
 * document physically look like", which drives the capture guide's aspect
 * ratio, its orientation, the shimmer silhouette and the instructions hero.
 *
 * The document catalog (`GET /v3/valid_documents`) carries a `format` integer
 * on every `id_type` and on every `sub_types[]` entry. That integer is the
 * authoritative signal. Before it existed, each component re-derived the shape
 * from the free-text `document-type` string with its own matching rule — six
 * call sites, three different rules, and the South Africa Green Book
 * unreachable in all of them (its `code` is `IDENTITY_CARD`, not
 * `GREEN_BOOK`). `resolveDocumentShape()` replaces all of that.
 *
 * Shape keys deliberately stay the three the detection engine already knows
 * (`id-card` / `passport` / `greenbook`). New formats map onto an existing
 * bucket rather than adding a fourth, so `useCardDetection`'s tolerance and
 * fill-percentage branches need no changes — adding a format is a one-line
 * edit to `FORMAT_SHAPES` below.
 */

/**
 * `format` values served by `GET /v3/valid_documents`, as defined by the
 * document-format spec. The full table, so the next spec revision has one place
 * to land — the SDK only acts on the physical shape each format implies:
 *
 * | `format` | ISO type | document                            | dimensions (mm) | ratio | spec shimmer                |
 * | -------- | -------- | ----------------------------------- | --------------- | ----- | --------------------------- |
 * | 0        | ID-000   | mini-SIM card                       | 25.00 × 15.00   | 1.67  | —                           |
 * | 1        | ID-1     | national IDs & DLs, photo on left   | 85.60 × 53.98   | 1.59  | `national_id_left_shimmer`  |
 * | 2        | ID-2     | visas, legacy European IDs (A7)     | 105.00 × 74.00  | 1.42  | `passport`                  |
 * | 3        | ID-3     | passport booklets (TD3)             | 125.00 × 88.00  | 1.42  | `passport`                  |
 * | 4        | A4       | sheet paper documents               | 210.00 × 297.00 | 0.71  | `A4_shimmer`                |
 * | 5        | ID-1     | photo on right (e.g. Ghana Card)    | 85.60 × 53.98   | 1.59  | `national_id_right_shimmer` |
 * | 6        | —        | Zambia National Registration Cert.  | —               | —     | `square_card_shimmer`       |
 * | 7        | —        | booklets (e.g. SA Green Book)       | —               | —     | `green_book_shimmer`        |
 * | 8        | —        | undefined document type             | —               | —     | `national_id_left_shimmer`  |
 *
 * The spec names each shimmer after a document; we name ours after the shape it
 * draws, because one silhouette serves several formats. Four assets cover the
 * nine: formats 4 and 6 borrow the booklet one, and format 5 mirrors the card
 * one (see `document-auto-capture/assets/shimmers.ts`). Outstanding with design
 * is a booklet **back** page: the square-card format is the one format in this
 * bucket the catalog serves with `has_back`, and with no back asset to switch
 * to its back step shows the front silhouette.
 */
// Named for the shape each format describes rather than for a country's
// document, since a format is a physical class and picks up new documents over
// time — format 5 is every right-photo ID-1 card, not only Ghana's, and
// format 6 is every square-ish card, not only Zambia's NRC.
//
// Keys are alphabetical throughout this module to satisfy the `sort-keys` lint
// rule, so the numeric values below are deliberately out of order.
export const DOCUMENT_FORMAT = Object.freeze({
  /** 4 — A4 sheet paper documents, read upright. */
  A4: 4,
  /** 7 — portrait booklets, e.g. the South Africa Green Book. */
  BOOKLET: 7,
  /** 1 — default for all ID-1 cards: driver's licenses, national IDs, voter IDs, residence cards. */
  CARD: 1,
  /** 5 — ID-1 cards with the photo on the right, e.g. the Ghana Card. */
  CARD_PHOTO_RIGHT: 5,
  /** 0 — mini-SIM cards, present so the enum matches the ISO series. */
  MINI_SIM: 0,
  /** 3 — all passports, including seaman's books. */
  PASSPORT: 3,
  /**
   * 6 — square-ish cards, e.g. the Zambia National Registration Card. The spec
   * gives it no ISO type or dimensions and there is no square shape bucket, so
   * it is guided with the portrait booklet shape for now.
   */
  SQUARE_CARD: 6,
  /**
   * 8 — "undefined document type": the API knows the format field is required
   * but not what the document is. Deliberately absent from `FORMAT_SHAPES`
   * below, which is what `normalizeDocumentFormat` tests, so it resolves as no
   * signal at all rather than as a shape. See `resolveDocumentShape`.
   */
  UNDEFINED: 8,
  /** 2 — ID-2: visas and legacy European IDs. */
  VISA: 2,
});

/**
 * Aspect ratios keyed by shape, always expressed **landscape** (long edge over
 * short edge). Portrait shapes are rendered by inverting this at the point of
 * use, so a single number describes the document either way.
 */
export const SHAPE_ASPECT_RATIOS = Object.freeze({
  greenbook: 1.42,
  /** ID-1 card ratio (ISO/IEC 7810). */
  'id-card': 1.585,
  passport: 1.42,
});

/**
 * `greenbook` is really the portrait bucket — it serves A4 and the square-card
 * format as well as the Green Book. Renaming it to `booklet` is worth doing but the
 * string reaches the detector's branch conditions in nine files, so it is
 * tracked as its own change rather than folded in here.
 */
export const DOCUMENT_SHAPES = Object.freeze({
  GREENBOOK: 'greenbook',
  ID_CARD: 'id-card',
  PASSPORT: 'passport',
});

/**
 * format → shape. `portrait` documents are guided upright; everything else is
 * captured landscape. `mirrored` flips the shimmer silhouette horizontally, for
 * documents that are the same shape as another but laid out the other way round.
 *
 * Every format collapses onto one of the three shape keys rather than growing a
 * fourth: `ASPECT_RATIOS` feeds the detector's tolerance, ROI masking and
 * quality gating in a dozen places, and `classifyDiscoveryAspect` assumes a
 * two-way id-card/passport split. Two of the mappings are therefore
 * approximations — noted per entry, both well inside the detector's tolerance.
 *
 * NOTE: format 6 is still mapped by physical resemblance rather than by spec —
 * the spec table lists no dimensions for it, so "square-ish card read upright"
 * borrowing the portrait booklet guide is our inference. If a true square
 * bucket ever ships, change it here and nowhere else.
 */
const FORMAT_SHAPES = Object.freeze({
  [DOCUMENT_FORMAT.CARD]: { key: DOCUMENT_SHAPES.ID_CARD, portrait: false },
  [DOCUMENT_FORMAT.PASSPORT]: {
    key: DOCUMENT_SHAPES.PASSPORT,
    portrait: false,
  },
  /** Same ID-1 shape as CARD; only the photo side differs, hence `mirrored`. */
  [DOCUMENT_FORMAT.CARD_PHOTO_RIGHT]: {
    key: DOCUMENT_SHAPES.ID_CARD,
    mirrored: true,
    portrait: false,
  },
  [DOCUMENT_FORMAT.SQUARE_CARD]: {
    key: DOCUMENT_SHAPES.GREENBOOK,
    portrait: true,
  },
  [DOCUMENT_FORMAT.BOOKLET]: {
    key: DOCUMENT_SHAPES.GREENBOOK,
    portrait: true,
  },
  /** ID-000 is 1.67, guided at the 1.585 card ratio. */
  [DOCUMENT_FORMAT.MINI_SIM]: { key: DOCUMENT_SHAPES.ID_CARD, portrait: false },
  /** ID-2 is 105 × 74 = 1.419 — the passport ratio for our purposes. */
  [DOCUMENT_FORMAT.VISA]: { key: DOCUMENT_SHAPES.PASSPORT, portrait: false },
  /** A4 portrait is 0.707 against the greenbook bucket's 0.704. */
  [DOCUMENT_FORMAT.A4]: { key: DOCUMENT_SHAPES.GREENBOOK, portrait: true },
});

/**
 * Legacy fallback: derive a shape from the `document-type` string when no
 * `format` is supplied. Preserves the pre-`format` behaviour exactly, so
 * integrations that never send a format are unaffected.
 *
 * Matches the id_type `code` values the API serves (`PASSPORT`) as well as the
 * shape-ish strings older hosts passed (`GREEN_BOOK`, `ID_CARD`). Unknown
 * strings fall through to `id-card`, which is what every previous classifier
 * defaulted to.
 * @param {unknown} documentType
 * @returns {{ key: string, portrait: boolean }}
 */
const shapeFromDocumentType = (documentType) => {
  const value = String(documentType ?? '')
    .trim()
    .toLowerCase();

  if (value.includes('green') && value.includes('book')) {
    return FORMAT_SHAPES[DOCUMENT_FORMAT.BOOKLET];
  }
  if (value.includes('passport')) {
    return FORMAT_SHAPES[DOCUMENT_FORMAT.PASSPORT];
  }
  return FORMAT_SHAPES[DOCUMENT_FORMAT.CARD];
};

/**
 * Coerce a `format` to a known key, or null. Accepts the numeric string an
 * HTML attribute inevitably delivers (`'7'`).
 *
 * Only numbers and strings are admitted, and blank strings are rejected before
 * coercion: `0` is a real format, so the usual `Number()` sloppiness (`''`,
 * `' '`, `false`, `[]` all becoming `0`) would otherwise turn junk input into a
 * confident mini-SIM.
 * @param {unknown} format
 * @returns {number | null}
 */
export const normalizeDocumentFormat = (format) => {
  if (typeof format !== 'number' && typeof format !== 'string') return null;
  const candidate = typeof format === 'string' ? format.trim() : format;
  if (candidate === '') return null;
  const parsed = Number(candidate);
  if (!Number.isInteger(parsed)) return null;
  return Object.prototype.hasOwnProperty.call(FORMAT_SHAPES, parsed)
    ? parsed
    : null;
};

/**
 * Resolve a document's physical shape.
 *
 * @param {unknown} format `format` from the catalog. A recognised value wins
 *   outright; anything else (absent, empty, unknown) falls back to
 *   `documentType`.
 * @param {unknown} [documentType] the `document-type` string, used only as the
 *   legacy fallback. Note that format 8 ("undefined") counts as "anything
 *   else": it carries no shape information, so it defers here rather than
 *   overriding a `documentType` the host may have been specific about.
 * @returns {{ key: 'id-card' | 'passport' | 'greenbook',
 *   orientation: 'landscape' | 'portrait', aspectRatio: number,
 *   mirrored: boolean }}
 *   `aspectRatio` is the ratio as rendered — already inverted for portrait
 *   shapes, so consumers can apply it directly. `mirrored` asks for a
 *   horizontally flipped silhouette.
 */
export const resolveDocumentShape = (format, documentType) => {
  const normalized = normalizeDocumentFormat(format);
  const shape =
    normalized === null
      ? shapeFromDocumentType(documentType)
      : FORMAT_SHAPES[normalized];

  const landscapeRatio = SHAPE_ASPECT_RATIOS[shape.key];

  return {
    aspectRatio: shape.portrait ? 1 / landscapeRatio : landscapeRatio,
    key: shape.key,
    mirrored: Boolean(shape.mirrored),
    orientation: shape.portrait ? 'portrait' : 'landscape',
  };
};
